import { useEffect, useState } from 'react';
import {
  Plus,
  X,
  AlertTriangle,
  Loader2,
  Settings,
  Shield,
  RefreshCw,
} from 'lucide-react';
import {
  useAIProviders,
  fetchProviderModels,
  isLocalProviderUrl,
  effectiveUseCorsProxy,
  parseManualModelList,
  isMixedContentLocalBlock,
  mixedContentOllamaHelp,
  pageIsHttps,
} from '@/hooks/useAIProviders';
import { useVault } from '@/hooks/useVault';
import type { AIProvider } from '@/contexts/AppContext';
import { PROVIDER_PRESETS, type ProviderPreset } from '@/lib/providerPresets';

interface ProviderFormData {
  name: string;
  baseUrl: string;
  apiKey: string;
  useCorsProxy: boolean;
  apiKeyOptional?: boolean;
}

/** AI providers — embeddable in the settings modal. */
export function ProviderSettingsPanel({ active = true }: { active?: boolean }) {
  const { providers, addProvider, removeProvider, updateProvider } = useAIProviders();
  const { hasVault, unlocked } = useVault();
  const keysUnsealed = !hasVault || !unlocked;
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<ProviderFormData>({
    name: '',
    baseUrl: '',
    apiKey: '',
    useCorsProxy: false,
  });
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [providerErrors, setProviderErrors] = useState<Record<string, string>>({});
  const [manualModels, setManualModels] = useState<Record<string, string>>({});
  const [showManual, setShowManual] = useState<Record<string, boolean>>({});

  const formIsLocal = isLocalProviderUrl(form.baseUrl);
  const anyLocalHttpOnHttps = providers.some((p) => isMixedContentLocalBlock(p.baseUrl));
  const showMixedBanner =
    anyLocalHttpOnHttps ||
    (pageIsHttps() && formIsLocal && form.baseUrl.trim().startsWith('http:'));

  useEffect(() => {
    if (!active) return;
    for (const p of providers) {
      if (p.useCorsProxy && isLocalProviderUrl(p.baseUrl)) {
        updateProvider(p.id, { useCorsProxy: false });
      }
    }
  }, [active, providers, updateProvider]);

  const acknowledgeCorsProxy = (): boolean => {
    return confirm(
      'CORS PROXY WARNING\n\n' +
        'When enabled, your API key and full chat requests (including system instructions and messages) are sent through a third-party proxy (proxy.shakespeare.diy by default).\n\n' +
        'That proxy can read everything in the request. Only enable if the provider blocks browser CORS and you accept this risk.\n\n' +
        'Prefer providers that allow direct browser calls, or run a local proxy you control.\n\n' +
        'Enable CORS proxy anyway?',
    );
  };

  const setCorsProxyChecked = (checked: boolean) => {
    if (checked && !formIsLocal) {
      if (!acknowledgeCorsProxy()) return;
    }
    setForm((f) => ({ ...f, useCorsProxy: checked }));
  };

  const applyPreset = (preset: ProviderPreset) => {
    setForm({
      name: preset.name,
      baseUrl: preset.baseUrl,
      apiKey: '',
      // Never auto-enable proxy — user must opt in with warning
      useCorsProxy: false,
      apiKeyOptional: preset.apiKeyOptional,
    });
    setIsAdding(true);
    setEditingProvider(null);
    setTestError(null);
  };

  const handleAdd = async () => {
    if (!form.name.trim() || !form.baseUrl.trim()) return;
    if (!form.apiKeyOptional && !form.apiKey.trim()) return;

    if (!hasVault) {
      const ok = confirm(
        'Local encryption is off. This API key will be stored in plaintext in this browser. Enable encryption in the Privacy tab first (recommended), or type CONTINUE to store plaintext anyway.',
      );
      if (!ok) return;
      const typed = window.prompt('Type CONTINUE to store API keys without encryption:');
      if (typed !== 'CONTINUE') return;
    } else if (!unlocked) {
      alert('Unlock the vault before saving API keys so they can be sealed at rest.');
      return;
    }

    let baseUrl = form.baseUrl.trim();
    if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
    const local = isLocalProviderUrl(baseUrl);
    // Proxy already acknowledged when the checkbox was turned on
    const useProxy = local ? false : form.useCorsProxy;

    const newProvider = addProvider({
      name: form.name.trim(),
      baseUrl,
      apiKey: form.apiKey.trim() || 'ollama',
      useCorsProxy: useProxy,
    });

    try {
      setTestingProvider(newProvider.id);
      setTestError(null);
      const models = await fetchProviderModels(newProvider);
      updateProvider(newProvider.id, { models, modelsFetchedAt: Date.now() });
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Failed to fetch models');
      setProviderErrors((prev) => ({
        ...prev,
        [newProvider.id]: err instanceof Error ? err.message : 'Failed to fetch models',
      }));
    } finally {
      setTestingProvider(null);
    }

    setForm({ name: '', baseUrl: '', apiKey: '', useCorsProxy: false });
    setIsAdding(false);
  };

  const handleTest = async (provider: AIProvider) => {
    const fixed =
      provider.useCorsProxy && isLocalProviderUrl(provider.baseUrl)
        ? { ...provider, useCorsProxy: false }
        : provider;
    if (fixed.useCorsProxy !== provider.useCorsProxy) {
      updateProvider(provider.id, { useCorsProxy: false });
    }
    try {
      setTestingProvider(provider.id);
      setProviderErrors((prev) => ({ ...prev, [provider.id]: '' }));
      setTestError(null);
      const models = await fetchProviderModels(fixed);
      updateProvider(provider.id, {
        models,
        modelsFetchedAt: Date.now(),
        useCorsProxy: fixed.useCorsProxy,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch models';
      setProviderErrors((prev) => ({ ...prev, [provider.id]: msg }));
    } finally {
      setTestingProvider(null);
    }
  };

  const handleEditSave = (provider: AIProvider) => {
    if (form.apiKey.trim()) {
      if (!hasVault) {
        const ok = confirm(
          'Local encryption is off. This API key will be stored in plaintext in this browser. Continue anyway?',
        );
        if (!ok) return;
      } else if (!unlocked) {
        alert('Unlock the vault before updating API keys.');
        return;
      }
    }
    let baseUrl = form.baseUrl.trim();
    if (baseUrl.endsWith('/')) baseUrl = baseUrl.slice(0, -1);
    const local = isLocalProviderUrl(baseUrl);
    updateProvider(provider.id, {
      name: form.name.trim(),
      baseUrl,
      apiKey: form.apiKey.trim(),
      useCorsProxy: local ? false : form.useCorsProxy,
    });
    setEditingProvider(null);
    setForm({ name: '', baseUrl: '', apiKey: '', useCorsProxy: false });
    void handleTest({
      ...provider,
      baseUrl,
      apiKey: form.apiKey.trim() || provider.apiKey,
      useCorsProxy: local ? false : form.useCorsProxy,
    });
  };

  const startEdit = (provider: AIProvider) => {
    setForm({
      name: provider.name,
      baseUrl: provider.baseUrl,
      apiKey: provider.apiKey,
      useCorsProxy: provider.useCorsProxy,
    });
    setEditingProvider(provider.id);
    setIsAdding(false);
  };

  const saveManualModels = (provider: AIProvider) => {
    const text = manualModels[provider.id] ?? provider.models.map((m) => m.id).join('\n');
    const models = parseManualModelList(text, provider.id);
    if (models.length === 0) {
      setProviderErrors((prev) => ({
        ...prev,
        [provider.id]: 'Enter at least one model name',
      }));
      return;
    }
    updateProvider(provider.id, {
      models,
      modelsFetchedAt: Date.now(),
    });
    setProviderErrors((prev) => ({ ...prev, [provider.id]: '' }));
    setShowManual((s) => ({ ...s, [provider.id]: false }));
  };

  return (
    <div className="space-y-4">
          <p className="text-[12px] text-[#5a5a5a] leading-relaxed">
            Add OpenAI-compatible API providers. Keys stay in this browser and are sent only to the
            provider URL you configure (or through a CORS proxy if you enable it — that is a
            full MITM). Shakespeare AI is built-in via signed Nostr requests (your npub is linked
            to usage).
          </p>

          {keysUnsealed && (
            <div className="bg-yellow-950/25 border border-yellow-900/40 rounded-xl p-3 flex gap-2.5">
              <Shield className="w-4 h-4 text-yellow-500/80 shrink-0 mt-0.5" />
              <div className="text-[11px] text-[#8a8070] leading-relaxed space-y-1">
                <p className="text-yellow-500/90 font-medium">
                  {!hasVault ? 'Local encryption is off' : 'Vault is locked'}
                </p>
                <p>
                  {!hasVault
                    ? 'API keys will be stored in plaintext until you enable encryption in the Privacy tab.'
                    : 'Unlock the vault before adding or updating keys so they stay sealed at rest.'}
                </p>
              </div>
            </div>
          )}

          {showMixedBanner && (
            <div className="bg-red-950/30 border border-red-900/40 rounded-xl p-3 flex gap-2.5">
              <AlertTriangle className="w-4 h-4 text-red-400/90 shrink-0 mt-0.5" />
              <div className="text-[11px] text-[#c4a0a0] leading-relaxed space-y-2">
                <p className="text-red-400/90 font-medium">
                  Local Ollama is not available from this HTTPS site
                </p>
                <p>{mixedContentOllamaHelp()}</p>
                <p>
                  <span className="text-[#e8e6e3]">What works here:</span> cloud APIs
                  (Shakespeare AI, OpenRouter, OpenAI, Groq, …) — HTTPS to HTTPS.
                </p>
                <p>
                  <span className="text-[#e8e6e3]">What works for local models:</span> run
                  OpenDork yourself over HTTP on this machine (
                  <code className="text-[#b8b0a8]">npm run dev</code>
                  ), with Ollama on localhost.
                </p>
              </div>
            </div>
          )}

          {!isAdding && !editingProvider && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#3a3a3a] mb-2">
                Quick add
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PROVIDER_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className={
                      preset.id === 'custom'
                        ? 'px-2.5 py-1.5 text-[11px] rounded-lg border border-dashed border-[#3a3a3a] text-[#8a8070] hover:text-[#e8e6e3] hover:border-[#5a5a5a] transition-colors'
                        : 'px-2.5 py-1.5 text-[11px] rounded-lg border border-[#2a2a2a] text-[#5a5a5a] hover:text-[#e8e6e3] hover:border-[#3a3a3a] transition-colors'
                    }
                    title={preset.hint}
                  >
                    {preset.id === 'custom' ? '+ Custom' : preset.name}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#3a3a3a] mt-2 leading-relaxed">
                Any OpenAI-compatible API works — set name, base URL ending in{' '}
                <code className="text-[#5a5a5a]">/v1</code>, and key. Use{' '}
                <span className="text-[#5a5a5a]">+ Custom</span> when your provider isn&apos;t
                listed.
              </p>
            </div>
          )}

          {providers.length === 0 && !isAdding && (
            <div className="text-center py-4">
              <p className="text-xs text-[#5a5a5a]">No custom providers yet</p>
            </div>
          )}

          {providers.map((provider) => (
            <div
              key={provider.id}
              className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-3.5 space-y-2.5"
            >
              {editingProvider === provider.id ? (
                <div className="space-y-2">
                  <h3 className="text-xs font-medium text-[#e8e6e3]">Edit Provider</h3>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Name"
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                  <input
                    type="text"
                    value={form.baseUrl}
                    onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                    placeholder="https://api.example.com/v1"
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                  <input
                    type="password"
                    value={form.apiKey}
                    onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                    placeholder="API Key"
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                  <label
                    className={`flex items-start gap-2 ${formIsLocal ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      checked={formIsLocal ? false : form.useCorsProxy}
                      disabled={formIsLocal}
                      onChange={(e) => setCorsProxyChecked(e.target.checked)}
                      className="mt-0.5 rounded border-[#2a2a2a] bg-[#111111] text-[#e8e6e3] focus:ring-0"
                    />
                    <span className="text-[11px] text-[#5a5a5a] leading-relaxed">
                      Use CORS proxy
                      {formIsLocal
                        ? ' (off for localhost)'
                        : ' — sends API key + chat body through a third party'}
                    </span>
                  </label>
                  {form.useCorsProxy && !formIsLocal && (
                    <p className="text-[10px] text-yellow-500/80 leading-relaxed pl-6">
                      Proxy can read your key and every message. Prefer direct provider access.
                    </p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => handleEditSave(provider)}
                      className="flex-1 bg-[#e8e6e3] text-[#0a0a0a] text-xs font-medium py-2 rounded-lg hover:bg-white transition-colors"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingProvider(null);
                        setForm({ name: '', baseUrl: '', apiKey: '', useCorsProxy: false });
                      }}
                      className="px-4 py-2 text-xs text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-[#e8e6e3] truncate">
                        {provider.name}
                      </span>
                      {provider.models.length > 0 ? (
                        <span className="text-[10px] bg-green-900/30 text-green-400/70 px-1.5 py-0.5 rounded shrink-0">
                          {provider.models.length} models
                        </span>
                      ) : (
                        <span className="text-[10px] bg-red-900/20 text-red-400/50 px-1.5 py-0.5 rounded shrink-0">
                          no models
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        onClick={() => startEdit(provider)}
                        className="p-1.5 text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
                        title="Edit"
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => void handleTest(provider)}
                        disabled={testingProvider === provider.id}
                        className="p-1.5 text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors disabled:opacity-50"
                        title="Refresh models"
                      >
                        {testingProvider === provider.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => removeProvider(provider.id)}
                        className="p-1.5 text-[#5a5a5a] hover:text-red-400 transition-colors"
                        title="Remove provider"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[#3a3a3a]">URL</span>
                      <span className="text-[#5a5a5a] truncate max-w-[200px]">
                        {provider.baseUrl}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[#3a3a3a]">Key</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[#5a5a5a] font-mono">
                          {showApiKey[provider.id]
                            ? provider.apiKey
                            : '•'.repeat(Math.min(provider.apiKey.length, 24))}
                        </span>
                        <button
                          onClick={() =>
                            setShowApiKey((prev) => ({
                              ...prev,
                              [provider.id]: !prev[provider.id],
                            }))
                          }
                          className="text-[#3a3a3a] hover:text-[#5a5a5a] transition-colors"
                        >
                          <span className="text-[10px]">
                            {showApiKey[provider.id] ? 'hide' : 'show'}
                          </span>
                        </button>
                      </div>
                    </div>
                    {effectiveUseCorsProxy(provider.baseUrl, provider.useCorsProxy) && (
                      <div className="text-[10px] text-yellow-500/80 leading-relaxed">
                        CORS proxy on — API key and chat bodies pass through a third party
                      </div>
                    )}
                    {isMixedContentLocalBlock(provider.baseUrl) && (
                      <div className="text-[10px] text-red-400/70 leading-relaxed pt-0.5">
                        Blocked here: HTTPS page cannot call http://localhost. See the banner
                        above — use a cloud provider on this site, or run OpenDork locally over
                        HTTP for Ollama.
                      </div>
                    )}
                    {isLocalProviderUrl(provider.baseUrl) &&
                      !isMixedContentLocalBlock(provider.baseUrl) && (
                        <div className="text-[10px] text-[#5a5a5a] leading-relaxed pt-0.5">
                          Local endpoint — set OLLAMA_ORIGINS=&quot;*&quot; if discovery fails.
                        </div>
                      )}
                  </div>

                  {providerErrors[provider.id] && provider.models.length === 0 && (
                    <div className="text-[10px] text-red-400/80 leading-relaxed bg-red-950/20 border border-red-900/30 rounded-md px-2 py-1.5">
                      {providerErrors[provider.id]}
                    </div>
                  )}
                  {isMixedContentLocalBlock(provider.baseUrl) && provider.models.length > 0 && (
                    <div className="text-[10px] text-yellow-500/70 leading-relaxed bg-yellow-950/15 border border-yellow-900/25 rounded-md px-2 py-1.5">
                      Model names are saved for the picker only. Chat still cannot reach
                      localhost from this HTTPS page.
                    </div>
                  )}

                  {provider.models.length > 0 && (
                    <div className="pt-1">
                      <div className="text-[10px] text-[#3a3a3a] mb-1">Available models:</div>
                      <div className="flex flex-wrap gap-1">
                        {provider.models.map((m) => (
                          <span
                            key={m.id}
                            className="text-[10px] bg-[#111111] text-[#5a5a5a] px-1.5 py-0.5 rounded border border-[#2a2a2a]"
                          >
                            {m.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-1 border-t border-[#1a1a1a] space-y-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        setShowManual((s) => ({
                          ...s,
                          [provider.id]: !s[provider.id],
                        }))
                      }
                      className="text-[10px] text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
                    >
                      {showManual[provider.id] || provider.models.length === 0 ? '▾ ' : '▸ '}
                      Add models manually
                      {provider.models.length === 0 ? ' (for the picker)' : ''}
                    </button>
                    {(showManual[provider.id] || provider.models.length === 0) && (
                      <div className="space-y-1.5">
                        <textarea
                          value={
                            manualModels[provider.id] ??
                            provider.models.map((m) => m.id).join('\n')
                          }
                          onChange={(e) =>
                            setManualModels((prev) => ({
                              ...prev,
                              [provider.id]: e.target.value,
                            }))
                          }
                          placeholder={'gemma3:4b\nllama3.2'}
                          rows={3}
                          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-2.5 py-2 text-[11px] font-mono text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a] resize-y"
                        />
                        <p className="text-[9px] text-[#3a3a3a]">
                          One name per line (from ollama list). Does not unlock localhost from
                          HTTPS.
                        </p>
                        <button
                          type="button"
                          onClick={() => saveManualModels(provider)}
                          className="w-full py-1.5 text-[11px] border border-[#2a2a2a] rounded-lg text-[#e8e6e3] hover:border-[#3a3a3a] transition-colors"
                        >
                          Save model list
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {isAdding && (
            <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-3.5 space-y-3">
              <div>
                <h3 className="text-xs font-medium text-[#e8e6e3]">
                  {form.name.trim() ? `Add ${form.name.trim()}` : 'Add provider'}
                </h3>
                {!form.baseUrl.trim() && (
                  <p className="text-[10px] text-[#5a5a5a] mt-1 leading-relaxed">
                    OpenAI-compatible: base URL should include{' '}
                    <code className="text-[#8a8070]">/v1</code> (we call{' '}
                    <code className="text-[#8a8070]">/v1/chat/completions</code> and{' '}
                    <code className="text-[#8a8070]">/v1/models</code>). LiteLLM, vLLM, LM Studio,
                    Azure OpenAI-style gateways, and most &quot;OpenAI-compatible&quot; hosts work.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-[11px] text-[#5a5a5a] block mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. My gateway"
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-[#5a5a5a] block mb-1">Base URL</label>
                  <input
                    type="text"
                    value={form.baseUrl}
                    onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
                    placeholder="https://api.example.com/v1"
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-[#5a5a5a] block mb-1">
                    API Key{form.apiKeyOptional ? ' (optional)' : ''}
                  </label>
                  <input
                    type="password"
                    value={form.apiKey}
                    onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                    placeholder={
                      form.apiKeyOptional
                        ? 'sk-… (or leave blank if the server ignores auth)'
                        : 'sk-…'
                    }
                    className="w-full bg-[#111111] border border-[#2a2a2a] rounded-lg px-3 py-2 text-xs text-[#e8e6e3] placeholder:text-[#3a3a3a] focus:outline-none focus:border-[#3a3a3a]"
                  />
                </div>
                <label
                  className={`flex items-start gap-2 ${formIsLocal ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    checked={formIsLocal ? false : form.useCorsProxy}
                    disabled={formIsLocal}
                    onChange={(e) => setCorsProxyChecked(e.target.checked)}
                    className="mt-0.5 rounded border-[#2a2a2a] bg-[#111111] text-[#e8e6e3] focus:ring-0"
                  />
                  <span className="text-[11px] text-[#5a5a5a] leading-relaxed">
                    Use CORS proxy
                    {formIsLocal
                      ? ' (off for localhost)'
                      : ' — third party sees your API key and chat'}
                  </span>
                </label>
                {form.useCorsProxy && !formIsLocal && (
                  <p className="text-[10px] text-yellow-500/80 leading-relaxed">
                    Only enable if the provider blocks browser CORS and you accept MITM risk.
                  </p>
                )}
                {formIsLocal && pageIsHttps() && (
                  <p className="text-[10px] text-red-400/70 leading-relaxed">
                    localhost will not work from this HTTPS page. Use a cloud API here, or run
                    OpenDork over HTTP locally for Ollama.
                  </p>
                )}
              </div>

              {testError && (
                <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-2">
                  <p className="text-[11px] text-red-400">{testError}</p>
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => void handleAdd()}
                  disabled={
                    !form.name.trim() ||
                    !form.baseUrl.trim() ||
                    (!form.apiKeyOptional && !form.apiKey.trim())
                  }
                  className="flex-1 bg-[#e8e6e3] text-[#0a0a0a] text-xs font-medium py-2 rounded-lg hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Add Provider
                </button>
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setForm({ name: '', baseUrl: '', apiKey: '', useCorsProxy: false });
                    setTestError(null);
                  }}
                  className="px-4 py-2 text-xs text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!isAdding && !editingProvider && (
            <button
              type="button"
              onClick={() =>
                applyPreset({
                  id: 'custom',
                  name: '',
                  baseUrl: '',
                  hint: '',
                  useCorsProxy: false,
                  apiKeyOptional: true,
                })
              }
              className="flex items-center gap-1.5 text-sm text-[#5a5a5a] hover:text-[#e8e6e3] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add custom provider
            </button>
          )}
    </div>
  );
}
